import { CircularProgress, Typography } from '@material-ui/core';
import { Backdrop, useNotificationModal } from '@room-match/shared-ui-components';
import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { useConfirmationModal } from 'shared/components/ConfirmationModal';
import Messaging from 'shared/components/Messaging/Messaging';
import { ROUTES } from 'shared/constants/ROUTES';
import { TextareaAutosize } from 'shared/elements/TextareaAutosize';
import { SelftapesSteps } from 'shared/enums/SelftapesSteps';
import { useSendMessage } from 'shared/hooks/useSendMessage';
import { IAuditionScheduleAttributes } from 'shared/interfaces/IAuditionSchedule';
import { useAppSelector } from 'shared/redux/store';
import { auditionScheduleService } from 'shared/services/auditionScheduleService';
import { auditionService } from 'shared/services/auditionService';
import { ls } from 'shared/utils/ls';
import { auditionTimeParse, STTimeDateParser, timeConvert, timeParser } from 'shared/utils/timeHelpers';
import {
  ButtonCont,
  ConfirmButton,
  DueCont,
  Heading,
  MainContainer,
  NoteCont,
  ResponseButton,
  SectionCont,
  SectionTitle,
} from './Response.styles';
import STLayout from './STLayout';
import StudioLayout from './StudioLayout';

interface Props {
  isSelftape?: boolean;
  data: IAuditionScheduleAttributes;
}

type IButtonKey = 'audition_request' | 'upcoming_auditions' | 'self_tapes' | 'audition_history';

enum ResponseType {
  unset,
  positive,
  negative,
  neutral,
}

const { updateAuditionSchedule } = auditionScheduleService();
const { getAudition } = auditionService();

const Response: React.FC<Props> = ({ isSelftape, data }) => {
  const { setLS } = ls();
  const { tab } = useParams() as { tab: IButtonKey };
  const { user } = useAppSelector((state) => state);

  const stageName =
    user?.user?.attributes.stage_name || `${user.user?.attributes.first_name} ${user.user?.attributes.last_name}`;
  const history = useHistory();

  const [response, setResponse] = useState<ResponseType>(ResponseType.unset);
  const [note, setnote] = useState('');
  const [responded, setresponded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hideResponse, setHideResponse] = useState(false);
  const { mutateAsync: mutateAsyncForwardAuditionSchedule, isLoading } = updateAuditionSchedule();
  const noteField: any = React.useRef();
  const queryClient = useQueryClient();
  const [audID, setaudID] = useState('');
  const { data: audData, isLoading: gettingAudData } = getAudition(audID);
  const { handleOpen, isOpen, ConfirmationModal, handleSetMessage, handleChangeModalType } = useConfirmationModal({
    type: 'success',
  });

  const projectId = data.audition.casting_role.project.id;

  const {
    handleOpen: handleNotifOpen,
    isOpen: isNotifOpen,
    NotificationModal,
    handleSetMessage: notifMessage,
    handleChangeModalType: notifChangeType,
  } = useNotificationModal({
    type: 'warning',
  });

  const confirmSubmit = () => {
    setaudID(data.id);
  };

  const showSuccess = () => {
    handleChangeModalType('success');
    handleSetMessage('Success', `Your response has been submitted. \n Do you want to submit your Self-tapes now?`);
    handleOpen();
  };

  const respond = async (isPositive?: boolean) => {
    setLoading(true);

    const noteKey =
      response === ResponseType.positive
        ? ''
        : response === ResponseType.negative
          ? 'declined_note'
          : 'reschedule_note';
    await mutateAsyncForwardAuditionSchedule(
      {
        audition_schedules: [
          {
            id: data.id,
            status:
              isPositive || response === ResponseType.positive
                ? 'Confirmed'
                : response === ResponseType.negative
                  ? 'Declined'
                  : 'Reschedule',
            [noteKey]: note,
            phase:
              (isPositive || response === ResponseType.positive) && data.phase === 'Self Tapes Process'
                ? 'Self Tapes Request'
                : data.phase,
          },
        ],
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries(['auditionSchedule'], {
            refetchInactive: true,
          });
          await queryClient.invalidateQueries(['audition_schedule'], {
            refetchInactive: true,
          });
          await queryClient.invalidateQueries(['auditionRequest'], {
            refetchInactive: true,
          });
          await queryClient.invalidateQueries(['me'], {
            refetchInactive: true,
          });
          if (response !== ResponseType.positive && !isPositive) {
            console.log('XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX');
            // await send();
          }
          if (isSelftape && (isPositive || response === ResponseType.positive)) {
            showSuccess();
          }
          setresponded(true);
          setLoading(false);
        },
      },
    );
  };

  const handleClick = (res: ResponseType) => {
    setResponse(res);
    setTimeout(() => {
      document.getElementById('myNote')?.focus();
      setnote('');
      if (res === ResponseType.negative) {
        setnote(data.declined_note || '');
      } else if (res === ResponseType.neutral) {
        setnote(data.reschedule_note || '');
      } else {
        respond(true);
      }
    }, 100);
  };

  useEffect(() => {
    if (data) {
      if (data.status.toLowerCase().includes('declined')) {
        setResponse(ResponseType.negative);
        setnote(data.declined_note || '');
      } else if (data.status.toLowerCase().includes('reschedule')) {
        setResponse(ResponseType.neutral);
        setnote(data.reschedule_note || '');
      } else if (data.status.toLowerCase() !== 'forwarded') {
        setResponse(ResponseType.positive);
        setresponded(true);
      }
    }
  }, [data]);

  useEffect(() => {
    if (!isLoading && audData?.data && audID !== '') {
      if (!audData.data.attributes.audition.data.attributes.casting_role.data.attributes.self_tapes_requirements) {
        notifChangeType('error');
        notifMessage(
          'Currently Unavailable',
          `You are unable to submit a self tape at this time as no requirements have been set yet by the casting director, please check with your agency or the casting contact directly.`,
          'OK',
        );
        handleNotifOpen();
        return;
      }
      setLS('auditionData', JSON.stringify(audData?.data));
      history.push(
        `${ROUTES.TALENT.SELFTAPES}/${data.audition.casting_role.project.id}/${audID}/${SelftapesSteps.SIZE_CARD}`,
      );
    }
  }, [audData, isLoading, audID]);

  if (gettingAudData || loading) return <Backdrop isLoading />;

  const role = data.audition.casting_role;
  const isNonRepre = data.audition.profile.agency ? false : true;

  const headshotUrl = data?.audition?.profile?.primary_medium_attachment?.modified_attachment || null;
  return (
    <React.Fragment>
      <MainContainer>
        <Heading>
          {!isSelftape ? 'Studio Audition for ' : 'ST Audition for '} {stageName}
        </Heading>
        <DueCont>
          {headshotUrl && (
            <SectionCont>
              <SectionTitle>Photo submitted by your representative:</SectionTitle>
              <img src={headshotUrl} style={{ width: '100%', maxWidth: 250, margin: '20px 0' }} />
            </SectionCont>
          )}
          <SectionCont>
            <SectionTitle>Role:</SectionTitle>
            <Typography style={{ fontWeight: 700 }}>{role.name}</Typography>
          </SectionCont>
          <SectionCont>
            <SectionTitle>Project Name:</SectionTitle>
            <Typography style={{ fontWeight: 700 }}>{role.project.name}</Typography>
          </SectionCont>
          <SectionCont>
            <SectionTitle>{isSelftape ? 'Submission Deadline:' : 'Audition Date & Time'}</SectionTitle>
            <Typography style={{ fontWeight: 700 }}>
              {data.phase === 'Self Tapes Request'
                ? STTimeDateParser(
                    data.audition.casting_role.self_tapes_requirements?.date || '',
                    data.audition.casting_role.self_tapes_requirements?.time || '',
                    data.audition.casting_role.self_tapes_requirements?.timezone || '',
                  )
                : timeConvert(data)}
            </Typography>
            <Typography>
              {isSelftape
                ? timeParser(
                    data.audition.casting_role.self_tapes_requirements?.date || '',
                    data.audition.casting_role.self_tapes_requirements?.time || '',
                    data.audition.casting_role.self_tapes_requirements?.timezone || '',
                  )
                : auditionTimeParse(data)}
            </Typography>
          </SectionCont>
          {role.wardrobe ? (
            <SectionCont>
              <SectionTitle>Wardrobe Notes:</SectionTitle>
              <Typography style={{ fontWeight: 700 }}>{role.wardrobe}</Typography>
            </SectionCont>
          ) : (
            <React.Fragment />
          )}
        </DueCont>

        {isSelftape ? (
          <STLayout data={role} />
        ) : (
          <StudioLayout
            data={data}
            note={data?.schedule?.casting_session?.request_note}
            location={data?.schedule?.casting_session?.casting_location?.address?.full_address}
          />
        )}
        {(data.status.toLowerCase().includes('declined') && tab === 'audition_history') ||
        tab !== 'audition_history' ? (
          <>
            <Heading style={{ fontSize: 16, marginTop: 20 }}>Your Response</Heading>
            <ButtonCont>
              <ResponseButton
                active={response === ResponseType.positive}
                onClick={() => handleClick(ResponseType.positive)}
                design="positive"
                disabled={
                  (responded && tab === 'audition_request') ||
                  Boolean(data?.phase === 'Self Tapes Process' && data?.self_tapes_requirement_revision)
                }
              >
                {isSelftape ? "Yes, I'll Submit" : " Yes, I'll Attend"}
              </ResponseButton>
              {!isSelftape && (
                <ResponseButton
                  active={response === ResponseType.neutral}
                  onClick={() => handleClick(ResponseType.neutral)}
                  design="neutral"
                  disabled={responded && tab === 'audition_request'}
                >
                  Request Time Change
                </ResponseButton>
              )}
              <ResponseButton
                active={response === ResponseType.negative}
                onClick={() => handleClick(ResponseType.negative)}
                design="negative"
                disabled={responded && tab === 'audition_request'}
              >
                {isSelftape ? 'Decline' : 'No, Unavailable'}
              </ResponseButton>
            </ButtonCont>
          </>
        ) : null}
        {(response === ResponseType.negative || response === ResponseType.neutral) && !hideResponse && (
          <NoteCont>
            <TextareaAutosize
              style={{ minHeight: 30 }}
              id="myNote"
              ref={noteField}
              disabled={responded && tab === 'audition_request'}
              label="Leave a note:"
              minRows={5}
              name="selections"
              value={note}
              onChange={(e) => setnote(e.target.value)}
            />
          </NoteCont>
        )}
        {(response === ResponseType.negative || response === ResponseType.neutral) &&
          (!hideResponse || response === ResponseType.negative) && (
            <ConfirmButton
              variant="contained"
              color="primary"
              onClick={() => respond()}
              disabled={(isLoading && tab === 'audition_request') || (!note && !hideResponse)}
            >
              {isLoading ? <CircularProgress style={{ color: 'white', width: 30, height: 30 }} /> : 'Confirm Response'}
            </ConfirmButton>
          )}
      </MainContainer>
      {responded && response === ResponseType.positive && isSelftape ? (
        <ConfirmButton
          variant="contained"
          color="primary"
          onClick={() => setaudID(data.id)}
          style={{
            marginTop: 0,
          }}
        >
          <Typography style={{ color: '#fff' }}>Submit Your Self-tapes Now</Typography>
        </ConfirmButton>
      ) : (
        React.Fragment
      )}
      <Messaging
        messageKey={isNonRepre ? 'casting_talent_messages' : 'agent_talent_messages'}
        projectId={projectId}
        isTalent
        // showEmpty={data.audition.profile.agency !== null}
        showEmpty
        type="new-audition-requests"
        id={data.id}
      />
      {isOpen && <ConfirmationModal onConfirm={confirmSubmit} confirmLabel="Submit Now" />}
      {isNotifOpen && (
        <NotificationModal
          onClose={() => {
            setaudID('');
          }}
        />
      )}
    </React.Fragment>
  );
};

export default Response;
